import { Core, Decorative } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Lock } from "@faire/web/slate/icons/foundation/Lock";
import { Row } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { Tooltip } from "@faire/web/slate/Tooltip";
import {
  TooltipTypography,
  Typography,
  TypographyStyles,
} from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { useSettingFaireDirectPDPSignup } from "@faire/retailer/settings/useSettingFaireDirectPDPSignup";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

export const LoggedOutPrice: React.FC<{
  retailPrice: string;
}> = ({ retailPrice }) => {
  const { isUK: isUKRetailer } = useRetailerStore(["isUK"]);
  const settingFaireDirectPDPSignup = useSettingFaireDirectPDPSignup();

  return (
    <>
      <Spacer size="2x" />
      <Row align="center" gap="2x" data-test-id="product-price">
        {settingFaireDirectPDPSignup ? (
          <LockedWholesalePriceV2 />
        ) : (
          <LockedPriceRow gap={4} justify="center" align="center">
            <Typography
              variant="paragraphSansRegular"
              color={Decorative.shade.orange}
            >
              <WholesalePriceText />
            </Typography>
            <Lock
              lockType="foundation"
              color={Decorative.shade.orange}
              data-test-id="logged-out-price-lock"
            />
          </LockedPriceRow>
        )}
        <ProductPrice>
          {isUKRetailer ? (
            <StrictLocalMsg
              defaultMessage="RRP {retailAmountMoney}"
              values={{
                retailAmountMoney: retailPrice,
              }}
              description={{
                text: "Recommended Retailer Price (RRP)",
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="MSRP {retailAmountMoney}"
              values={{
                retailAmountMoney: retailPrice,
              }}
              description={{
                text: "Manufacturer's suggested retail price of the product",
              }}
            />
          )}
        </ProductPrice>
      </Row>
    </>
  );
};

const LockedWholesalePriceV2: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { isMobile } = useViewport();

  return (
    <Row gap={4} justify="center" align="center">
      {isMobile ? (
        <Typography variant="paragraphSansRegular">
          <WholesalePriceText />
        </Typography>
      ) : (
        <Tooltip
          width={226}
          position="top"
          contents={[
            {
              body: strictLocalize({
                defaultMessage: "Sign up for Faire to unlock wholesale pricing",
                description: {
                  text: "Users cannot see the wholesale pricing (WSP) until they have created a Faire account.",
                  img: "https://cdn.faire.com/fastly/ebba36934476719440fe0b69a427868369055ada9ba236227b15c2dd95ce852b.png",
                },
              }),
            },
          ]}
          targetNode={
            <TooltipTypography>
              <WholesalePriceText />
            </TooltipTypography>
          }
        />
      )}
      <Lock lockType="foundation" data-test-id="logged-out-price-lock" />
    </Row>
  );
};

const WholesalePriceText: React.FC = () => (
  <StrictLocalMsg
    defaultMessage="WSP"
    description={{
      text: "Abbreviation for wholesale price of the product",
      img: "https://cdn.faire.com/fastly/9e62efc329493a652881f4044dc877d9432ae9465bc263bc6e942b7dfe246fff.png",
    }}
  />
);

const ProductPrice = styled.h1`
  ${(props) =>
    TypographyStyles({
      variant: "paragraphSansRegular",
      color: Core.text.default,
      theme: props.theme,
    })};
`;

const LockedPriceRow = styled(Row)`
  background-color: ${Decorative.tint.orange};
  padding: ${getSpacing(4, "2x")};
`;
